import { Group, TextInput } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import i18n from '../../../../../base-dictionary';
import { STANDARD_DATE_FORMATS } from '../../../../../helpers/date-time-helpers';
import { useRelatedTasksTabStore, useStrategyStore } from '../../../../../store/strategy-store/strategy-store';
import {
  setRelatedTasksSearchTerm,
  setRelatedTasksSelectedDate,
} from '../../../../../store/strategy-store/strategy-store.actions';
import { baseWhite, inkLight, skyDark } from '../../../../../styles/design-tokens';
import { Button } from '../../../../../ui-library/button/button';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Text } from '../../../../../ui-library/typography/typography';
import { getLatestDate } from '../related-tasks-tab.helpers';
import { useFilteredTasks, useSearchedTasks } from '../related-tasks-tab.hooks';
import { handleTasksDownloadCSV } from './main-section.helpers';

export const MainSectionControls = () => {
  const initiativePerformance = useStrategyStore((state) => state.initiativePerformance);
  const selectedDate = useRelatedTasksTabStore((state) => state.selectedDate);
  const searchTerm = useRelatedTasksTabStore((state) => state.searchTerm);
  const filteredTasks = useFilteredTasks();
  const searchedTasks = useSearchedTasks();

  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  useEffect(() => {
    if (!selectedDate) {
      setRelatedTasksSelectedDate(getLatestDate(initiativePerformance));
    }
  }, [initiativePerformance, selectedDate]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setRelatedTasksSearchTerm(value);
      }, 200),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  return (
    <Group justify="space-between" wrap="nowrap">
      <Group gap={16} wrap="nowrap">
        <Text size="large" weight="bold" style={{ whiteSpace: 'nowrap' }}>
          {i18n.t('strategy.initiative_performance.related_work_units')}
        </Text>
        <MonthPickerInput
          radius="xl"
          style={{ width: 160 }}
          rightSection={<Icon name="calendar_month" color={inkLight} size={16} />}
          rightSectionPointerEvents="none"
          placeholder={i18n.t('strategy.initiative_performance.pick_date')}
          value={selectedDate}
          valueFormat={STANDARD_DATE_FORMATS.MONTH_YEAR}
          onChange={(value) => {
            if (value) {
              const localDate = dayjs(value).startOf('month').toDate();
              setRelatedTasksSelectedDate(localDate);
            }
          }}
          maxDate={
            initiativePerformance?.effective_end_date
              ? dayjs(initiativePerformance?.effective_end_date).toDate()
              : dayjs(initiativePerformance?.end_date).toDate()
          }
          clearable={false}
        />
      </Group>
      <Group justify="flex-end" wrap="nowrap" style={{ width: '100%' }}>
        {filteredTasks.length > 0 && (
          <TextInput
            placeholder={i18n.t('strategy.initiative_performance.search_placeholder')}
            radius="xl"
            size="sm"
            value={localSearchTerm}
            leftSection={<Icon name="search" size={16} color={inkLight} />}
            style={{ minWidth: 216, maxWidth: 300, width: '100%' }}
            onChange={(event) => {
              const value = event.currentTarget.value;
              setLocalSearchTerm(value);
              debouncedSearch(value);
            }}
          />
        )}
        <Button
          variant="secondary"
          radius="xl"
          size="sm"
          leftSection={
            <Icon
              name="download"
              color={searchedTasks.length === 0 ? skyDark : baseWhite}
              variant="filled"
              size={16}
              style={{ cursor: searchedTasks.length === 0 ? 'not-allowed' : 'pointer' }}
            />
          }
          onClick={() => handleTasksDownloadCSV(searchedTasks)}
          style={{ minWidth: 100 }}
          disabled={searchedTasks.length === 0}
        >
          {i18n.t('common.actions.export')}
        </Button>
      </Group>
    </Group>
  );
};
